import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import authService from "../../../services/authService";
import creditService from "../../../services/creditService";
import orderService from "../../../services/orderService";
import {
  LayoutGrid,
  Globe,
  TicketCheck,
  User,
  LogOut,
  Menu,
  X,
  Coins,
  Settings,
  // eslint-disable-next-line no-unused-vars
  History,
  RefreshCw
} from "lucide-react";

const DashboardLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [credits, setCredits] = useState({ support: 0, development: 0 });
  // eslint-disable-next-line no-unused-vars
  const [creditHistory, setCreditHistory] = useState([]);
  const [isRecalculating, setIsRecalculating] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [showCreditHistory, setShowCreditHistory] = useState(false);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [subscriptionRefreshTrigger, setSubscriptionRefreshTrigger] = useState(0);
  
  // Define handleRecalculateCredits with useCallback before it's used
  const handleRecalculateCredits = useCallback(async () => {
    console.log("Recalculating credits");
    try {
      setIsRecalculating(true);
      const response = await creditService.recalculateCredits(user?.id);
      
      if (response && response.success) {
        // Refresh credits after recalculation
        const updatedCredits = await creditService.getRemainingCredits(user?.id);
        
        if (updatedCredits && updatedCredits.success) {
          setCredits({
            support: updatedCredits.data.support_credits || 0,
            development: updatedCredits.data.custom_credits || 0
          });
        }
      }
    } catch (error) {
      console.error("Error recalculating credits:", error);
    } finally {
      setIsRecalculating(false);
    }
  }, [user?.id]);

  // Define fetchActiveSubscription outside of useEffect
  const fetchActiveSubscription = useCallback(async () => {
    try {
      const response = await orderService.getActiveSubscription();
      if (response && response.success) {
        if (response.has_active_subscription) {
          const subscriptionData = response.subscription;
          
          if (subscriptionData.metadata && typeof subscriptionData.metadata === 'string') {
            try {
              subscriptionData.metadata = JSON.parse(subscriptionData.metadata);
            } catch (e) {
              console.error("Failed to parse subscription metadata string:", e);
            }
          }
          
          const processedSubscription = {
            ...subscriptionData,
            amount: subscriptionData.amount || subscriptionData.amount_paid,
            start_date: subscriptionData.start_date || subscriptionData.started_at || subscriptionData.created_at,
            plan_name: subscriptionData.metadata?.plan_name || 
                      subscriptionData.product_name || 
                      subscriptionData.product?.name
          };
          
          setActiveSubscription(processedSubscription);
        } else {
          setActiveSubscription(null);
        }
      } else {
        setActiveSubscription(null);
      }
    } catch (error) {
      console.error("Error fetching active subscription:", error);
      setActiveSubscription(null);
    }
  }, []);

  // Create a refresh function to pass to children
  const refreshSubscription = useCallback(() => {
    console.log('Refreshing subscription data in DashboardLayout...');
    setSubscriptionRefreshTrigger(prev => prev + 1);
  }, []);

  // Fetch initial data
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await authService.getCurrentUser();
        if (response && response.user) {
          setProfile(response.user);
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      }
    };

    const fetchCredits = async () => {
      try {
        const response = await creditService.getRemainingCredits(user?.id);
        if (response && response.success) {
          setCredits({
            support: response.data.support_credits || 0,
            development: response.data.custom_credits || 0
          });
        }
      } catch (error) {
        console.error("Error fetching credits:", error);
        setCredits({
          support: 0,
          development: 0
        });
      }
    };

    const fetchCreditHistory = async () => {
      try {
        const response = await creditService.getCreditHistory(user?.id, { limit: 5 });
        if (response && response.success) {
          setCreditHistory(response.data.credit_history || []);
        }
      } catch (error) {
        console.error("Error fetching credit history:", error);
        setCreditHistory([]);
      }
    };

    fetchUserProfile();
    fetchCredits();
    fetchActiveSubscription();
    fetchCreditHistory();
  }, [user?.id, fetchActiveSubscription]);

  // Add effect to recalculate credits when component mounts
  useEffect(() => {
    let isMounted = true;

    const initialRecalculation = async () => {
      if (isMounted && user?.id) {
        console.log("Initial credit recalculation");
        await handleRecalculateCredits();
      }
    };

    initialRecalculation();

    return () => {
      isMounted = false;
    };
  }, [handleRecalculateCredits, user?.id]); // Include dependencies

  // Add effect to handle credit updates from TicketDetail
  useEffect(() => {
    const handleCreditUpdate = async (event) => {
      // eslint-disable-next-line no-unused-vars
      const { userId, credits } = event.detail;
      
      // Only update if the user ID matches
      if (userId === user?.id) {
        console.log("Credit update received from ticket confirmation");
        await handleRecalculateCredits();
      }
    };

    window.addEventListener('dashboardCreditsUpdate', handleCreditUpdate);

    return () => {
      window.removeEventListener('dashboardCreditsUpdate', handleCreditUpdate);
    };
  }, [user?.id, handleRecalculateCredits]);

  // Effect to refresh subscription when trigger changes
  useEffect(() => {
    console.log('Subscription refresh triggered in DashboardLayout, trigger value:', subscriptionRefreshTrigger);
    fetchActiveSubscription();
  }, [subscriptionRefreshTrigger, fetchActiveSubscription]);

  // Pass the refresh function to children
  const childrenWithProps = React.useMemo(() => {
    if (!children || typeof children !== 'object') return children;
    
    return React.Children.map(children, child => {
      if (React.isValidElement(child)) {
        console.log('Passing refreshSubscription to child component');
        return React.cloneElement(child, { onSubscriptionUpdate: refreshSubscription });
      }
      return child;
    });
  }, [children, refreshSubscription]);

  // User display name - use profile data if available, otherwise fall back to user context
  const displayName = profile?.given_name || user?.name || 'User';
  const userInitial = (profile?.given_name || user?.name || 'U').charAt(0);
  
  // Check if user is admin
  const isAdmin = user?.role === 'admin' || profile?.role === 'admin';

  const navItems = [
    {
      name: "Dashboard",
      path: "/dashboard",
      icon: LayoutGrid,
    },
    {
      name: "Websites",
      path: "/dashboard/websites",
      icon: Globe,
    },
    {
      name: "Support Tickets",
      path: "/dashboard/tickets",
      icon: TicketCheck,
    },
    {
      name: "My Account",
      path: "/dashboard/settings?tab=account",
      icon: Settings,
    },
  ];
  
  // Add admin-only navigation items
  if (isAdmin) {
    navItems.push(
      {
        name: "Products",
        path: "/dashboard/admin/products",
        icon: Coins,
      },
      {
        name: "Tickets",
        path: "/dashboard/admin/tickets",
        icon: TicketCheck,
      }
    );
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 dashboard">
      {/* Mobile Header */}
      <header className="md:hidden sticky top-0 z-10 border-b bg-white py-3 px-4">
        <div className="flex items-center justify-between">
          <button
            className="p-2 rounded-md text-gray-600 hover:bg-gray-100"
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>
      </header>

      {/* Mobile Navigation Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-50 bg-white pt-16">
          <nav className="p-4 space-y-4 overflow-y-auto max-h-full">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2 rounded-md ${
                  location.pathname === item.path
                    ? "bg-blue-600 text-white"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
                onClick={closeMobileMenu}
              >
                <item.icon className="h-5 w-5" />
                <span>{item.name}</span>
              </Link>
            ))}
            <hr className="my-4" />
            <Link
              to="/profile"
              className="flex items-center gap-3 px-3 py-2 rounded-md text-gray-700 hover:bg-gray-100"
              onClick={closeMobileMenu}
            >
              <User className="h-5 w-5" />
              <span>Profile</span>
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-3 py-2 rounded-md text-gray-700 hover:bg-gray-100 w-full text-left"
            >
              <LogOut className="h-5 w-5" />
              <span>Logout</span>
            </button>
          </nav>
        </div>
      )}

      {/* Main Layout Container */}
      <div className="flex flex-1 flex-col md:flex-row">
        {/* Sidebar Navigation - Properly sticky when scrolling */}
        <aside className="hidden md:block md:sticky md:top-0 md:self-start w-64 bg-white border-r h-screen overflow-y-auto">
          <div className="flex flex-col h-full">
            {/* Main Navigation */}
            <nav className="flex-1 p-4 space-y-2">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-2 rounded-md ${
                    location.pathname === item.path
                      ? "bg-blue-600 text-white"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  <span>{item.name}</span>
                </Link>
              ))}
            </nav>

            {/* Credits Display */}
            <div className="px-4 mt-auto">
              {/* Active Plan Display */}
              <div className="mb-4" id="subscription-section">
                {activeSubscription ? (
                  <div className="p-3 bg-gray-50 rounded-md border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-medium text-gray-500">CURRENT PLAN</p>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Active</span>
                    </div>
                    <p className="text-sm font-semibold">
                      {activeSubscription.metadata?.plan_name || 
                       activeSubscription.product_name || 
                       activeSubscription.product?.name || 
                       'Subscription'}
                    </p>
                    <div className="mt-1 flex items-center justify-between">
                      <p className="text-xs text-gray-500">
                        Renews: {new Date(activeSubscription.current_period_end || activeSubscription.ends_at).toLocaleDateString()}
                      </p>
                      <Link to="/dashboard/settings?tab=products" className="text-xs text-blue-600 hover:underline">
                        Manage
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 bg-amber-50 rounded-md border border-amber-200">
                    <p className="text-sm font-medium text-amber-800 mb-1">No active subscription</p>
                    <p className="text-xs text-amber-700 mb-2">Subscribe to get credits for support</p>
                    <Link 
                      to="/dashboard/settings?tab=products" 
                      className="w-full text-center block text-xs py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-md transition-colors"
                    >
                      Choose a plan
                    </Link>
                  </div>
                )}
              </div>
              
              <div className="rounded-md mb-4 space-y-3" id="credits-section">
                <div className="p-3 bg-blue-50 rounded-md border border-blue-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center mr-3">
                        <Coins className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-blue-800">SUPPORT CREDITS</p>
                        <p className="text-lg font-bold text-blue-700">{credits.support || 0}</p>
                      </div>
                    </div>
                    <button
                      onClick={handleRecalculateCredits}
                      disabled={isRecalculating}
                      className="p-1 rounded-full hover:bg-blue-100 transition-colors"
                      title="Recalculate credits"
                    >
                      <RefreshCw className={`h-4 w-4 text-blue-600 ${isRecalculating ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                </div>
                
                <div className="p-3 bg-emerald-50 rounded-md border border-emerald-200">
                  <div className="flex items-center">
                    <div className="h-8 w-8 rounded-full bg-emerald-600 flex items-center justify-center mr-3">
                      <Coins className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-emerald-800">CUSTOM DEVELOPMENT CREDITS</p>
                      <p className="text-lg font-bold text-emerald-700">{credits.development || 0}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* User Profile & Logout */}
            <div className="p-4 border-t">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-medium">
                  {userInitial}
                </div>
                <div className="truncate">
                  <p className="font-medium text-sm truncate">{displayName}</p>
                  <p className="text-xs text-gray-500 truncate">{user?.email || ''}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Logout
              </button>
            </div>
          </div>
        </aside>

        {/* Main Content - Scrollable */}
        <main className="flex-1 bg-gray-50 min-h-screen w-full overflow-auto">
          <div className="p-4 md:p-6">
            {childrenWithProps}
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;